import { z } from "zod";
import type { FinancialYear, RuleSetMeta } from "../types.js";

/**
 * packaging-shock — the salary-packaging / novated-lease "income-test shock"
 * tool (AU candidate `packaging-income-test-shock`).
 *
 * It is the consumer-facing storefront over the shared `income-tests` add-backs
 * engine: a user gives a salary plus ONE reportable fringe benefit (an ordinary
 * novated lease, or s57A exempt-employer packaging) and we trace the grossed-up
 * RFBA through the four means-tests it moves — Medicare levy surcharge (MLS),
 * Division 293, HELP/STSL repayment income, and the family-assistance adjusted
 * taxable income (CCS/FTB). For each we show BOTH sides: the income-tax saving
 * the packaging delivers AND the income-test flow-on it triggers.
 *
 * BINDING panel ship-conditions (encoded as code, not comments):
 *   - BOTH sides always: the output carries the income-tax saving AND the
 *     income-test downside. Never downside-only, never upside-only.
 *   - The exempt-vs-non-exempt asymmetry is load-bearing: the (1 − FBT) un-gross
 *     -up (factor 0.53) applies ONLY to s57A exempt-employer RFBA; an ordinary
 *     novated lease counts at FULL grossed-up value even in CCS/FTB ATI. We
 *     delegate this entirely to `income-tests` — never re-derive it here.
 *   - NO financial-product / novated-lease affiliate identifier may reach a
 *     personalised result surface (misleading-by-omission + AFSL s764A drift).
 *     `assertNoProductHandoff` guards this and a test asserts it.
 *
 * Pure: inputs -> rules -> outputs. No I/O, no Date.now(); the assessment FY is
 * always passed in.
 */

/** Which FBT class the packaged benefit falls under — drives the CCS/FTB asymmetry. */
export const PackagingKindSchema = z.enum([
  // Ordinary employer novated lease (or any non-exempt packaging). Counts at
  // FULL grossed-up value in ALL income tests, including CCS/FTB ATI.
  "novated_lease",
  // s57A exempt-employer packaging (PBI/HPC/public-or-NFP-hospital/ambulance).
  // CCS/FTB ATI un-grosses the RFBA by (1 − FBT rate); MLS/Div293/HELP still
  // count it in full.
  "exempt_employer",
]);
export type PackagingKind = z.infer<typeof PackagingKindSchema>;

export const PackagingShockInputSchema = z.object({
  /**
   * Salary (cash taxable income) the person would have WITHOUT any packaging —
   * i.e. before the benefit is sacrificed. The packaged cash comes out of this.
   */
  grossSalary: z.number().min(0),
  /** Which FBT class the benefit falls under (drives the CCS asymmetry). */
  packagingKind: PackagingKindSchema,
  /**
   * The TAXABLE value of the packaged benefit for the FBT year — the pre-gross
   * -up figure (an RFBA is reported only when this exceeds the $2,000 trigger).
   * The grossed-up RFBA that hits the income tests = this × gross-up rate.
   */
  benefitTaxableValue: z.number().min(0),
  /**
   * Cash salary actually sacrificed to fund the benefit (the take-home the
   * person forgoes). Reduces taxable income → this is what drives the income-
   * tax saving. Defaults to `benefitTaxableValue` when the packaged cash equals
   * the taxable value; callers can override (e.g. exempt packaging where the
   * net cash differs from the grossed-up cap).
   */
  sacrificedCash: z.number().min(0).optional(),
  /** Reportable (concessional) super contributions — add-back for every test. */
  reportableSuper: z.number().min(0).default(0),
  /** Single vs family — sets which MLS / CCS threshold table applies. */
  filingStatus: z.enum(["single", "family"]),
  /** Partner's income for surcharge purposes — combined for the family MLS tier. */
  partnerIncome: z.number().min(0).default(0),
  /** Partner's ATI for family-assistance — combined for the CCS income test. */
  partnerAti: z.number().min(0).default(0),
  /** Dependent children — shifts the family MLS threshold +$1,500 each after the first. */
  children: z.number().int().min(0).default(0),
  /** Whether the person holds an eligible private hospital policy (MLS-exempt). */
  holdsHospitalCover: z.boolean().default(false),
  /** Whether the person carries a HELP/STSL debt (drives the HELP flow-on). */
  hasHelpDebt: z.boolean().default(false),
});

export type PackagingShockInput = z.infer<typeof PackagingShockInputSchema>;

/** The four income figures, derived before and after the benefit is added. */
export interface IncomeFigures {
  incomeForSurchargePurposes: number;
  helpRepaymentIncome: number;
  div293Income: number;
  adjustedTaxableIncome: number;
  /** The grossed-up RFBA in this scenario (0 without the benefit). */
  grossedUpFringeBenefits: number;
  /** The adjusted fringe benefits total used inside ATI (exempt un-grossed). */
  adjustedFringeBenefitsTotal: number;
}

/** A single means-test traced from "without benefit" to "with benefit". */
export interface MeansTestTrace {
  /** Which test this is. */
  test: "mls" | "div293" | "help" | "ccs";
  /** The income figure WITHOUT the benefit. */
  incomeWithout: number;
  /** The income figure WITH the benefit. */
  incomeWith: number;
  /** The increase in the tested income the benefit caused (≥ 0). */
  incomeDelta: number;
  /** True when adding the benefit crosses a threshold/tier in this test. */
  crossesThreshold: boolean;
  /**
   * Human-stable tier label without/with the benefit (e.g. MLS "Base" → "Tier
   * 1", Div293 "below" → "at-or-above"). `null` for tests without tiers.
   */
  tierWithout: string | null;
  tierWith: string | null;
  /**
   * The dollar flow-on this test triggers WITH the benefit, where it can be
   * computed from the ruleset (MLS surcharge, extra HELP repayment). `null`
   * where the magnitude depends on data this tool doesn't hold (CCS rate%,
   * Div293 is a 15% super-contributions tax we don't size here).
   */
  flowOnAmount: number | null;
}

/**
 * The result surface payload. Carries BOTH sides by construction (panel ship-
 * condition): `incomeTaxSaving` is the upside, `means` the income-test flow-on.
 */
export interface PackagingShockOutput {
  /** The four income figures WITHOUT the packaged benefit. */
  without: IncomeFigures;
  /** The four income figures WITH the packaged benefit (RFBA added). */
  with: IncomeFigures;
  /**
   * The income-tax (+ Medicare levy) saving the packaging delivers — the
   * UPSIDE. Always present so the surface can never show downside-only.
   */
  incomeTaxSaving: number;
  /** Per-test flow-on traces — the income-test DOWNSIDE. Always present. */
  means: MeansTestTrace[];
  /**
   * True when the benefit moves the person across at least one means-test
   * threshold (any `means[i].crossesThreshold`). Convenience for copy.
   */
  crossesAnyThreshold: boolean;
}

/** RFBA reporting / gross-up + the means-test thresholds, FY-versioned. */
export interface PackagingShockRuleSet extends Omit<RuleSetMeta, "product"> {
  /** Shared-infra tag — not a single AU0x product. */
  product: "SHARED";
  /** The income-tests FY this ruleset pairs with (for the 0.53 factor etc.). */
  incomeTestsFy: FinancialYear;
  /** The income-tax FY this ruleset pairs with (for the tax-saving + HELP calc). */
  incomeTaxFy: FinancialYear;
  /**
   * Type-2 (lower) gross-up rate applied to a benefit's taxable value to get
   * the reported RFBA. 1.8868 for FBT years to 31 Mar 2027.
   */
  grossUpRate: number;
  /** RFBA reporting trigger — on the TAXABLE value, not the grossed-up figure. */
  rfbaReportingThreshold: number;
  /** MLS thresholds (single / family base, + per-child increment). */
  mls: {
    /** Single base threshold (Tier 1 starts above this). */
    singleBase: number;
    /** Family base threshold (Tier 1 starts above this). */
    familyBase: number;
    /** Added to the family threshold per MLS dependent child after the first. */
    familyChildIncrement: number;
  };
  /** Division 293 income threshold ($250k) — extra 15% on super above it. */
  div293Threshold: number;
}
